import { useEffect, useRef } from 'react';

import { get as getUser } from 'js/lib/user';

import { selectProgramHomepage } from 'bundles/page/api/selectProgramHomePage';
import type { UserExperienceSelection } from 'bundles/unified-common/contexts/UserExperienceContext';
import type { ProgramMembershipEdge } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';

/**
 * Syncs programSwitcherSelections.v1 when UserExperienceContext is enterprise.
 *
 * The server-renderer's HomepageRedirect reads userSwitcherSelections (which
 * reflects programSwitcherSelections) to redirect users on `/`. It only
 * understands PROGRAM/DEGREE — not enterprise orgs. This hook bridges the gap
 * by writing a PROGRAM selection (using a program within the user's org) so the
 * server-renderer sends enterprise users to `/programs/:programSlug`, where
 * EnterprisePageGuard handles the org-scoped redirect.
 *
 * This is a bridge until HOME-5116 migrates the server-renderer to read
 * UserExperienceContext directly.
 */
export function useSyncProgramSwitcherSelection(
  userExperience: UserExperienceSelection,
  programMemberships: ProgramMembershipEdge[] | undefined,
  isMembershipsLoading: boolean
): void {
  const lastSyncedOrgIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isMembershipsLoading) return;

    const userId = getUser()?.id;
    if (!userId) return;

    if (userExperience?.__typename === 'UserExperienceContext_EnterpriseSelection') {
      const orgId = userExperience.organization.id;
      if (orgId === lastSyncedOrgIdRef.current) return;

      const matchingEdge = programMemberships?.find(
        (edge) => edge?.node?.program?.naptimeThirdPartyOrganization?.id === orgId
      );
      const programId = matchingEdge?.node?.program?.id;
      if (programId) {
        lastSyncedOrgIdRef.current = orgId;
        selectProgramHomepage(String(userId), 'PROGRAM', programId).catch(() => {
          // Best-effort sync — silent failure. Server-renderer will fall through
          // to client-side redirect if programSwitcherSelections is stale.
          lastSyncedOrgIdRef.current = null;
        });
      }
    } else {
      // Clear the legacy selection when switching to consumer/degree so the server-renderer
      // doesn't bounce the user back to a program page on the next full navigation.
      if (lastSyncedOrgIdRef.current !== null) {
        selectProgramHomepage(String(userId), 'COURSERA').catch(() => {
          // Best-effort — same as the enterprise path.
        });
      }
      lastSyncedOrgIdRef.current = null;
    }
  }, [userExperience, programMemberships, isMembershipsLoading]);
}

/**
 * Clears the legacy programSwitcherSelections to indicate consumer context.
 *
 * Call this and await it BEFORE window.location.assign when switching to consumer,
 * because the useEffect in useSyncProgramSwitcherSelection may not fire before
 * the navigation starts. This is the synchronous counterpart for navigation-critical paths.
 */
export function clearProgramSwitcherSelection(): Promise<void> {
  const userId = getUser()?.id;
  if (!userId) return Promise.resolve();
  return Promise.resolve(selectProgramHomepage(String(userId), 'COURSERA')).catch(() => {
    // Best-effort — server-renderer will fall through to client-side redirect if stale.
  });
}
